import React from 'react';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useCharityTheme } from '../../../contexts/CharityThemeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';

const AboutMissionTitle = 'AboutMissionTitle';
const AboutMissionText = 'AboutMissionText';

export default function AboutMissionSection({ section }) {
  const { t } = useTranslations();
  const { c } = useContent();
  const { editMode } = useEditMode();
  const { charityName } = useCharityTheme();

  const missionTitle = c(AboutMissionTitle, '') || t('about.mission') || 'Our Mission';
  const missionText = c(AboutMissionText, '') || t('about.missionDesc') || `${charityName} is dedicated to strengthening our community through innovative lottery fundraising. Every ticket purchased directly supports local programs, initiatives, and causes that make a real difference in people's lives.`;

  const howItWorksTitle = t('about.howItWorks') || 'How It Works';

  const steps = [
    {
      icon: Ticket,
      title: t('about.step1Title') || 'Purchase a Ticket',
      description: t('about.step1Desc') || 'Choose your lucky numbers and support your community with every ticket you buy.',
    },
    {
      icon: Trophy,
      title: t('about.step2Title') || 'Winners Are Drawn',
      description: t('about.step2Desc') || 'Fair, transparent draws give everyone an equal chance to win exciting prizes.',
    },
    {
      icon: Heart,
      title: t('about.step3Title') || 'Community Funded',
      description: t('about.step3Desc') || 'Proceeds go directly to local programs and initiatives that strengthen our neighbourhood.',
    },
  ];

  return (
    <div className="mb-12 sm:mb-20 md:mb-24">
      <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-12 md:mb-16">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-6">
          <InlineTranslationEdit translationKey={AboutMissionTitle} value={missionTitle} />
        </h2>
        <p className="text-sm sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
          <InlineTranslationEdit translationKey={AboutMissionText} value={missionText} multiline />
        </p>
      </div>

      {/* How It Works */}
      <div className="max-w-5xl mx-auto">
        <h3 className="text-lg sm:text-2xl font-bold text-center text-gray-900 dark:text-white mb-6 sm:mb-12">
          {howItWorksTitle}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="relative text-center group bg-white dark:bg-gray-800/60 p-5 sm:p-8 rounded-2xl border border-gray-200/80 dark:border-gray-700/60 shadow-sm sm:shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                <div className="relative inline-block mb-3 sm:mb-6">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full shadow-lg transition-transform duration-300 group-hover:scale-110"
                    style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
                  >
                    <Icon className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <div className="absolute -top-1.5 -right-1.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center text-xs font-bold shadow-md">
                    {index + 1}
                  </div>
                </div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">{step.title}</h4>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-xs sm:text-sm pb-1">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
